// Account-backed settings sync.
//
// When signed in, the user's settings live in the `profiles.settings` jsonb
// column (one row per user, RLS-guarded so you can only read/write your own).
// On sign-in we PULL that row and apply it locally (the account is the source of
// truth across devices); while signed in, any local change is PUSHED back
// (debounced). A brand-new account with no saved settings is seeded from this
// device's current local settings.
//
// What syncs: audio/motion settings + player 1 & 2 (name/color/keys). Stats and
// the local match roster (maps, extra bots) stay device-local for now. ELO/stats
// will be separate, server-written columns - never client-written like these.

import type { User } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { getSettings, setSettings, subscribeSettings, type Settings } from "./settings";
import {
  loadLocalPlayer,
  applyLocalPlayers,
  subscribeLocalPlayers,
  type LocalPlayerPrefs,
} from "./local-players";

interface SyncedSettings {
  v: 1;
  audio: Settings;
  players: Array<LocalPlayerPrefs | null>;
}

const PUSH_DEBOUNCE_MS = 800;

// Local edits the account row hasn't received yet: "<uid>:<Date.now()>". Set
// the instant a local change happens, cleared once a push that started after
// it succeeds. On the next start-up a still-dirty device WINS over the account
// copy (it pushes instead of pulling), so equipping something and reloading
// inside the debounce window, or while offline, never reverts the equip.
const DIRTY_KEY = "ar.accountSettings.dirty";
function markDirty(uid: string): void {
  try {
    localStorage.setItem(DIRTY_KEY, `${uid}:${Date.now()}`);
  } catch {
    /* storage blocked; the debounced push is the only path then */
  }
}
function dirtySince(uid: string): number | null {
  try {
    const raw = localStorage.getItem(DIRTY_KEY);
    if (!raw) return null;
    const i = raw.lastIndexOf(":");
    if (raw.slice(0, i) !== uid) return null;
    return Number(raw.slice(i + 1)) || 0;
  } catch {
    return null;
  }
}
function clearDirtyBefore(uid: string, t: number): void {
  const since = dirtySince(uid);
  if (since === null || since > t) return; // a newer edit landed mid-push; it pushes again
  try {
    localStorage.removeItem(DIRTY_KEY);
  } catch {
    /* ignore */
  }
}

function collectLocal(): SyncedSettings {
  return { v: 1, audio: getSettings(), players: [loadLocalPlayer(0), loadLocalPlayer(1)] };
}

// Apply downloaded settings to the local stores (account wins). Guarded against
// partial/older payloads so a missing field never wipes a local one.
function applySynced(s: SyncedSettings | null | undefined): void {
  if (!s) return;
  if (s.audio) setSettings({ ...getSettings(), ...s.audio });
  if (Array.isArray(s.players)) applyLocalPlayers(s.players);
}

let stop: (() => void) | null = null;

async function pushNow(user: User): Promise<void> {
  if (!supabase) return;
  const startedAt = Date.now();
  const { error } = await supabase
    .from("profiles")
    .upsert({ id: user.id, settings: collectLocal(), updated_at: new Date().toISOString() });
  if (error) console.warn("[account-settings] save failed:", error.message);
  else clearDirtyBefore(user.id, startedAt);
}

// Start syncing for a signed-in user: pull (account wins), seed if empty, then
// push local changes back (debounced). Safe to call repeatedly - it resets.
export async function startAccountSync(user: User): Promise<void> {
  stopAccountSync();
  if (!supabase) return;

  if (dirtySince(user.id) !== null) {
    // This device has edits the account never received (reload inside the
    // debounce window, or made offline): they are the newest state, push them.
    await pushNow(user);
  } else {
    const { data, error } = await supabase
      .from("profiles")
      .select("settings")
      .eq("id", user.id)
      .maybeSingle();

    if (error) {
      // Network/permission hiccup: leave local settings untouched, don't push.
      return;
    }

    const remote = data?.settings as SyncedSettings | undefined;
    if (remote && (remote.audio || remote.players)) {
      applySynced(remote);
    } else {
      // No saved settings yet for this account - seed it from this device.
      await pushNow(user);
    }
  }

  // Push local changes back, debounced so a burst of edits is one write. The
  // dirty mark is set immediately so a reload before the push can't lose it.
  let timer: ReturnType<typeof setTimeout> | null = null;
  const flush = () => {
    if (!timer) return;
    clearTimeout(timer);
    timer = null;
    void pushNow(user);
  };
  const schedule = () => {
    markDirty(user.id);
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void pushNow(user);
    }, PUSH_DEBOUNCE_MS);
  };
  // Leaving the page (reload, tab close, navigation away) flushes a pending
  // push right away; if the request gets cut off, the dirty mark covers it.
  window.addEventListener("pagehide", flush);
  const unsubAudio = subscribeSettings(schedule);
  const unsubPlayers = subscribeLocalPlayers(schedule);
  stop = () => {
    if (timer) clearTimeout(timer);
    window.removeEventListener("pagehide", flush);
    unsubAudio();
    unsubPlayers();
  };
}

export function stopAccountSync(): void {
  if (stop) {
    stop();
    stop = null;
  }
}
